import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Container,
  Dropdown,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import { ArrowDown, ArrowUp, Funnel, Search } from "react-bootstrap-icons";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import { TextInput } from "../form-generator/components";
import { addOrUpdateItemInArray } from "../form-generator/helpers/utility";
import { ABORT_ERROR, makeRESTApiRequests } from "../helpers/api";
import {
  BROWSE_JOBS_SORT_OPTIONS,
  ENDPOINTS,
  PAGE_SIZE,
} from "../helpers/constants";
import { getLabelsByRole } from "../helpers/dataSheetConstants";
import {
  getColumnColor,
  getRowColor,
  requestFilterInstance,
  updateItemsInArray,
} from "../helpers/global";
import useAppChoices from "../hooks/useAppChoices";
import useAuth from "../hooks/useAuth";
import DataSheet from "./DataSheet";
import Filters from "./Filters";
import NotFound from "./NotFound";
import SlidingSideBar from "./SlidingSideBar/SlidingSideBar";
import DriveFileUploader from "./drive-file-uploader";

const initialCurrentPageInfoData = {
  pageNumber: 1,
  pageSize: PAGE_SIZE,
  totalPages: 1,
};

const BrowseJobs = ({ status = "", newJob = false, appChoices = [] }) => {
  const mainContainer = useRef();
  const mainRow = useRef();

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [sortBy, setSortBy] = useState(BROWSE_JOBS_SORT_OPTIONS[0]?.key);
  const [updatedJobs, setUpdatedJobs] = useState([]);
  const [jobsWithUpdatedValue, setJobsWithUpdatedValue] = useState([]);
  const [editMode, setEditMode] = useState(false);

  const [fieldStaffOptions, setFieldStaffOptions] = useState([]);

  const [sideBarVisible, setSideBarVisible] = useState(false);
  const [jobFilters, setJobFilters] = useState([]);
  const [descSort, setDescSort] = useState(
    !["Field Setup", "Field Work", "Drafting", "Reviewing"].includes(status)
  );
  const [fileUploadJobId, setFileUploadJobId] = useState(null);
  const [appraisalFileUploading, setAppraisalFileUploading] = useState(false);
  const [currentPageInfo, setCurrentPageInfo] = useState(
    initialCurrentPageInfoData
  );
  const [jobsUpdateInProgress, setJobsUpdateInProgress] = useState(false);
  const [filesToBeUploaded, setFilesToBeUploaded] = useState([]);
  const [fetchingMoreJobs, setFetchingMoreJobs] = useState(false);
  const [baseTableHeight, setBaseTableHeignt] = useState(null);
  const location = useLocation();
  const abortControllerRef = useRef(null);

  const usersAppChoices = useAppChoices("users");
  const { user } = useAuth();

  const onUploadedFilesChange = (files) => {
    setFilesToBeUploaded(files);
  };

  useEffect(() => {
    const fieldStaff = usersAppChoices
      ?.filter((user) => user?.role === "Field Staff")
      .map((user) => user?.name);

    setFieldStaffOptions([...fieldStaff]);
  }, []);

  const fetchJobs = async (loadingMoreJobs = false) => {
    // If an old API call is in progress, abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!loadingMoreJobs) {
      setCurrentPageInfo({ ...initialCurrentPageInfoData });
    }
    const search = document.getElementById("searchText");
    const filter = {
      query: search ? search.value : "",
      sortBy,
      sortDirection: descSort ? -1 : 1,
    };

    filter.filterBy = {};
    if (newJob) {
      filter.filterBy["newJobs"] = [true];
    }

    if (loadingMoreJobs) {
      setFetchingMoreJobs(true);
    }

    if (!loadingMoreJobs) {
      setLoading(true);
    }

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.GET_JOBS,
      requestBody: {
        ignoreLogs: true,
        filter: requestFilterInstance(jobFilters),
        sort: { [filter?.sortBy]: filter?.sortDirection },
        query: filter?.query,
        pageSize: PAGE_SIZE,
        pageNumber: !loadingMoreJobs
          ? initialCurrentPageInfoData?.pageNumber
          : currentPageInfo?.pageNumber,
      },
      signal,
    });

    if (error === ABORT_ERROR) return;

    if (error) {
      if (loadingMoreJobs) {
        setFetchingMoreJobs(false);
      }
      setLoading(false);
      return toast.error(error);
    }

    if (response) {
      setCurrentPageInfo({
        pageNumber: response?.pageNumber + 1,
        pageSize: response?.pageSize,
        totalPages: response?.totalPages,
      });

      if (loadingMoreJobs) {
        setJobs((prevJobs) => [...prevJobs, ...response?.results]);
      } else {
        setJobs([...response.results]);
      }
    }
    setLoading(false);
    if (loadingMoreJobs) {
      setFetchingMoreJobs(false);
    }
  };

  useEffect(() => {
    if (fieldStaffOptions.length > 0) fetchJobs();
  }, [sortBy, fieldStaffOptions, descSort]);

  const handleReturn = (e) => {
    if (e.key === "Enter") {
      fetchJobs();
    }
  };

  //must return true for success and false for not success
  const onSave = async (data) => {
    if (data.length === 0) {
      toast("Nothing to save!", {
        type: "info",
      });
      return false;
    }

    toast(`Please wait, saving data...`, {
      type: "info",
    });

    setJobsUpdateInProgress(true);

    data.forEach((elements) => {
      Object.keys(elements?.jobUpdates).forEach((element) => {
        if (elements?.jobUpdates[element] === "Unassigned") {
          elements.jobUpdates[element] = null;
        }
      });
    });

    const { error, response } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_BULK_JOBS,
      method: "PUT",
      requestBody: data,
    });

    if (error) {
      toast.error(error);
      setJobsUpdateInProgress(false);

      return false;
    }

    toast("Succesfully saved data!", {
      type: "success",
    });

    setJobs(updateItemsInArray(jobs, response?.updatedJobs));
    setUpdatedJobs([]);
    setJobsUpdateInProgress(false);

    return true;
  };

  const handleSortDirectionChange = () => {
    setDescSort(!descSort);
  };

  const onFileUploadModalHide = () => {
    setFileUploadJobId(null);
    setFilesToBeUploaded([]);
  };

  const onFileUploadClick = (job) => {
    setFileUploadJobId(job);
    setAppraisalFileUploading(false);
  };

  const onApprImageSave = async ({ fileId }) => {
    return new Promise(async (resolve, reject) => {
      try {
        const { response, error } = await makeRESTApiRequests({
          endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(fileUploadJobId?._id),
          requestBody: { appraisalImageId: fileId },
          method: "PUT",
        });

        if (error) {
          reject("Failure");
          return toast.error(error);
        }
        resolve("Success");
        const updatedJobIndex = jobsWithUpdatedValue.findIndex(
          (job) => job?._id === fileUploadJobId?._id
        );

        jobsWithUpdatedValue[updatedJobIndex] = response;

        setJobsWithUpdatedValue([...jobsWithUpdatedValue]);

        toast.success("Appraisal image successfully added");
      } catch (error) {
        reject("Failure");
      }
    });
  };

  const FileUploadModal = () => (
    <Modal
      show={fileUploadJobId != null}
      onHide={onFileUploadModalHide}
      centered
      backdrop="static"
    >
      <Modal.Header closeButton={!appraisalFileUploading}>
        Upload Appraisal File
      </Modal.Header>
      <Modal.Body>
        <DriveFileUploader
          multiple={false}
          id={"Upload-job-file"}
          filesToBeUploaded={filesToBeUploaded}
          onUploadedFilesChange={onUploadedFilesChange}
          onFileSave={onApprImageSave}
          deletable={false}
          folderId={fileUploadJobId?.driveFolder}
        />
      </Modal.Body>
    </Modal>
  );

  useEffect(() => {
    if (
      filesToBeUploaded.some(
        (fileToBeUploaded) => fileToBeUploaded?.status === "complete"
      )
    ) {
      onFileUploadModalHide();
    }
  }, [filesToBeUploaded]);

  const onEditClick = () => {
    setEditMode(true);
  };
  const onCancelClick = () => {
    setEditMode(false);
    setUpdatedJobs([]);
    setJobsWithUpdatedValue([...jobs]);
  };
  const onJobChange = (job) => {
    setJobsWithUpdatedValue((prevJobs) => {
      const clonedJobs = _.cloneDeep(prevJobs);
      const updatedJobs = addOrUpdateItemInArray(clonedJobs, job, "_id");
      return updatedJobs;
    });
    setUpdatedJobs((prevJobs) => {
      const clonedJobs = _.cloneDeep(prevJobs);
      const updatedJobs = addOrUpdateItemInArray(clonedJobs, job, "_id");
      return updatedJobs;
    });
  };

  const loadMoreData = () => {
    if (jobs?.length) {
      if (
        currentPageInfo?.pageNumber > currentPageInfo?.totalPages ||
        fetchingMoreJobs
      ) {
        return;
      }

      fetchJobs(true);
    }
  };

  useEffect(() => {
    setJobsWithUpdatedValue(jobs);
  }, [jobs]);

  const onFilterValueChange = (updatedFilters) => [
    setJobFilters(updatedFilters),
  ];

  useEffect(() => {
    const handleResize = () => {
      let mainDivElement = mainContainer.current;
      let mainRowElement = mainRow.current;

      setBaseTableHeignt(
        mainDivElement.offsetHeight - mainRowElement.offsetHeight - 140
      );
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Container
      fluid
      className={"py-3 h-100 d-flex flex-column"}
      ref={mainContainer}
    >
      <SlidingSideBar
        visible={sideBarVisible}
        onClose={() => setSideBarVisible(false)}
        title="Filter Jobs"
      >
        <Filters
          appChoices={appChoices}
          onFilterValueChange={onFilterValueChange}
          filterValues={jobFilters}
          onHide={() => setSideBarVisible(false)}
          executeSearch={() => fetchJobs()}
          status={status}
        />
      </SlidingSideBar>
      <Row className="px-md-3 mt-1 " id="main-row" ref={mainRow}>
        <Col xs={7}>
          <div className="input-group mb-1">
            <TextInput
              id="searchText"
              hint="Leave empty to view recent results"
              onKeyPress={handleReturn}
              size="sm"
            />
            <div className="input-group-append">
              <Button
                variant="outline-info"
                onClick={() => fetchJobs()}
                size="sm"
              >
                <Search className="align-text-top mr-2" size={16} />
                Search
              </Button>
            </div>
          </div>
        </Col>
        <Col xs={2} className="px-0">
          <Dropdown className="mb-1 mb-md-0">
            <Dropdown.Toggle
              block
              variant="outline-primary"
              id="dropdown-basic"
              size="sm"
            >
              Sort
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSortBy("updatedAt")}>
                Last Updated Date
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy("createdAt")}>
                Submission Date
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy("proposedDueDate")}>
                Proposed Due Date
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy("agreedDueDate")}>
                Agreed Due Date
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setSortBy("titleClosingDate")}>
                Title Closing Date
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>

        <Col xs={3}>
          <div className="w-50 d-inline-block pr-1">
            <Button block size="sm" onClick={handleSortDirectionChange}>
              {descSort ? (
                <ArrowDown className="text-align-top" />
              ) : (
                <ArrowUp className="text-align-top" />
              )}
            </Button>
          </div>
          <div className="w-50 d-inline-block">
            <Button
              variant="primary"
              block
              onClick={() => {
                setSideBarVisible(true);
              }}
              size="sm"
            >
              <Funnel className="align-text-top" />
            </Button>
          </div>
        </Col>
        <Col className="d-none d-md-block" xs={12}>
          <p style={{ fontSize: "14px" }} className="text-right my-1">
            <b>Sorting by: </b>
            <span className="text-secondary">
              {
                BROWSE_JOBS_SORT_OPTIONS.find(
                  (option) => option?.key === sortBy
                )?.label
              }
            </span>
          </p>
        </Col>
        {loading && (
          <Col xs={12} className="my-3">
            <ProgressBar now={100} animated label="Searching jobs..." />
          </Col>
        )}
      </Row>
      <div className="px-md-3 flex-grow-1 overflow-auto">
        {jobs.length > 0 ? (
          <>
            <DataSheet
              height={baseTableHeight}
              buttonDisabled={jobsUpdateInProgress}
              columns={getLabelsByRole(location, appChoices, user?.role)}
              data={jobsWithUpdatedValue}
              updatedData={updatedJobs}
              editMode={editMode}
              stickyFieldIndex={2}
              rowColor={(obj) => getRowColor(obj, location, status)}
              columnColor={(obj, field) => getColumnColor(obj, field, location)}
              onRowChange={onJobChange}
              onCancelClick={onCancelClick}
              onEditClick={onEditClick}
              onSave={onSave}
              onFileUploadClick={onFileUploadClick}
              loadMoreData={loadMoreData}
              fetchingMoreJobs={fetchingMoreJobs}
              validationField={[
                {
                  field: "Latitude",
                },
                {
                  field: "Longitude",
                },
              ]}
            />

            {FileUploadModal()}
          </>
        ) : (
          !loading && <NotFound text="No jobs found" />
        )}
      </div>
    </Container>
  );
};

export default BrowseJobs;
